import React from 'react'
import tw, { styled } from 'twin.macro'
import Footer from '../../components/elements/Footer'
import Navbar from '../../components/elements/Navbar'
import components from '../../content/common'
import { useBreakpoint } from '@hooks/useBreakpoint'

export const Section = styled.section`
  ${tw`w-full bg-radler pb-20 pt-20 lg:pb-40 lg:pt-56 overflow-hidden`}

  p {
    ${tw`text-white pt-5 text-15px lg:text-18px font-roboto`}
    b {
      ${tw``}
    }
  }

  a {
    ${tw`text-gelb`}
    &:hover {
      filter: drop-shadow(1px 2px 0.75rem rgba(247, 211, 3, 1));
    }
  }
`

export const Headline = tw.h2`text-32px md:text-5xl lg:text-75pxv2 text-white my-10`

export const Subtitle = tw.h3`font-DIN text-white text-left md:text-[32px] leading-[38px] text-2xl max-w-[300px] lg:p-0 pt-5`

export const ListWrap = tw.div`pt-5`
export const List = tw.ul`list-disc mx-5 lg:mx-10`
export const ListItem = tw.li`pb-2 lg:text-18px text-white`

const list = [
  'Communicating with you on products, services, e.g. to process your inquiries or to send an order confirmation;',
  'Planning, executing and administering the (contractual) business relationship between us and you, e.g. in order to process the order of products and services, collect payments, for accounting, invoicing and debt collection purposes and to carry out deliveries, or complaint handling, as well as for our customer service and inquiries',
  'Carrying out customer surveys, marketing campaigns, market analyses, raffles, competitions or similar campaigns and events',
  'Providing a newsletter and advertising for new products',
  'Creating pseudonymised usage profiles for the purpose of advertising, market research and improving our services',
  'Maintaining and protecting the security of our products, services and websites by preventing and detecting security risks, fraudulent activity or other criminal or malicious activities;',
  'Complying with legal requirements (e.g. tax and commercial law storage obligations) or existing obligations to carry out compliance screenings (to prevent economic crime or money laundering)',
  'Settling disputes, enforcing existing contracts and asserting, exercising and defending legal claims.',
]

const linkList = [
  {
    name: 'Mozilla Firefox',
    href: 'https://support.mozilla.org/de/kb/cookies',
  },
  {
    name: 'Internet Explorer',
    href: 'https://support.microsoft.com/de-de/topic/löschen-von-cookiedateien-in-internet-explorer-bca9446f-d873-78de-77ba-d42645fa52fc',
  },
  {
    name: 'Google Chrome',
    href: 'https://support.google.com/chrome/answer/95647?hl=de&co=GENIE.Platform%3DDesktop',
  },
  {
    name: 'Safari',
    href: 'https://support.apple.com/de-de/guide/safari/sfri11471/mac',
  },
]

const list2 = [
  'the IP address of the user, shortened by the last two bytes (anonymized)',
  'the subpage called and time of the call',
  'the page from which the user has reached our website (referrer)',
  'which browser with which plugins, which operating system and which screen resolution is used',
  'the length of stay on the website',
  'the pages that are accessed from the called subpage',
]

const list3 = [
  'You can completely disable the storage of cookies in your browser. However, this means that you may no longer be able to use some functions of our website that require identification (shopping cart, orders, personal settings, etc.).',
  'You can enable the "Do-not-track" setting in your browser. Our Matomo system is configured to respect this setting.',
  'You can create a so-called opt-out cookie with a mouse click below, which is valid for two years. It has the consequence that Matomo will not register your further visits. Note, however, that the opt-out cookie will be deleted if you delete all cookies.',
]

const IndexPage = () => {
  const { isMobile } = useBreakpoint()
  return (
    <>
      <Navbar content={components.navbar} />
      <Section>
        <div tw="max-w-screen-2xl mx-auto px-3 sm:px-7">
          <Headline>Data protection terms</Headline>
          <Subtitle>Data protection terms</Subtitle>
          <p>
            Data protection is of great importance to Privatbrauerei ERDINGER Weißbräu Werner Brombach GmbH, Lange Zeile
            1 + 3, 85435 Erding, Germany. For this reason, the processing of our customers' data is carried out
            exclusively in compliance with the applicable data protection regulations (e.g. GDPR, the new German Federal
            Data Protection Act BDSG-neu).
          </p>
          <p>
            We collect and process personal data if you provide us with this data (e.g. by registering on our website,
            placing an order, participating in a competition, becoming a Club and / or Team member, ordering our
            newsletter) and we are entitled to collect, use and process it on the basis of your consent or a legal
            regulation. If we receive personal data about you from other companies, you will be notified as soon as
            possible, at the latest upon first contact. These data are also only stored and processed in accordance with
            legal regulations.
          </p>
          <p>
            <b>Your personal data</b>
            <br />
            Personal data are any information relating to an identified or identifiable natural person (hereinafter
            referred to as the "data subject"). We collect and process the following personal data:
            <br />
            Name, first name, home address, phone number, date of birth,
            gender, e-mail address
          </p>
          <p>
            The above mentioned personal data are processed by us for the following purposes:
          </p>
          <ListWrap>
            <List>
              {list.map((item, index) => (
                <ListItem key={index}>{item}</ListItem>
              ))}
            </List>
          </ListWrap>
          <p>
            The processing of personal data is necessary to achieve the above-mentioned purposes, including the execution of the (contractual) business relationship with you. Unless explicitly stated otherwise, the legal basis for data processing is Article 6 (1)(b) and (f) of the General Data Protection Regulation (GDPR) or your explicit consent in accordance with Article 6 (1)(a) GDPR.
          </p>
          <p>
            If the above data are to be processed for purposes other than the original purpose of collection, you will be informed of this before further processing. This way you have the possibility to object to the processing of your data for another purpose.
          </p>
          <p>
            <b>Disclosure of data</b>
            <br />
            All data transferred to us will be treated confidentially. We will not sell or otherwise market your
            personal data to third parties.
          </p>
          <p>
            Your data will generally not be made available to third parties for use unless you have given your consent
            or we are legally entitled and/or obliged to do so.
          </p>
          <p>
            If legally permissible and necessary to comply with applicable law or to assert, exercise or defend legal
            claims, we transfer personal data to courts, tax authorities and supervisory authorities. We take all
            appropriate measures to ensure the protection of your personal data.
          </p>
          <p>
            We will forward your personal data to the following companies for the above-mentioned purposes:
          </p>
          <p>
            Mail order companies / logistic service providers, payment service providers, software service providers,
            financial service providers, banking institutions, sales agents, marketing event agencies, translation
            agencies, printing offices, broadcasting companies, newspapers, travel agencies, tour operators, travel
            companies, public sector bodies, e.g. the Federal Statistical Office, the Main Customs Office, chambers of
            commerce, insurance companies, central settlers, travel and transport companies such as Deutsche Bahn,
            airlines, coach companies
          </p>
          <p>
            <b>Duration of data storage</b>
            <br />
            If there is no explicit storage duration stated during data collection (e.g. in the context of a declaration
            of consent), personal data will be deleted if it is no longer required for the purpose of storage, unless
            required otherwise by law (e.g. commercial and tax law storage obligations).
          </p>
          <p>
            <b>Data security</b>
            <br/>
            We take technical and organisational actions to protect your data stored and processed in our company
            against manipulation, loss of confidentiality, destruction and against access by unauthorised persons. Our
            company's security measures are continuously improved in accordance with technological developments.
          </p>
          <p>
            <b>
              Data subjects' rights: right to information, correction, deletion or limitation of the processing of your
              personal data, right of objection and right to data transferability
            </b>
            <br/>
            Upon request, we will inform you in writing, in accordance with the applicable law, whether and which
            personal data we store in our company database. If you are registered as a user, we offer you the
            possibility to view your data yourself and to change and delete them if necessary. If, despite our company's
            efforts to ensure data security and data accuracy, incorrect information has been stored, we will correct it
            upon your request.
          </p>
          <p>
            You also have the right to demand that our company restricts the processing of your personal data. In
            addition, you can request that the data you provide to our company be delivered in a structured, common and
            machine-readable format. You may also object to the processing of personal data by our company.
          </p>
          <p>
            Furthermore, you have the right to demand the deletion of your personal data, provided that there are no
            conflicting legal retention periods. We delete the data if we no longer need them for the purpose for which
            we have collected and processed them, or if you revoke your consent and there is no other legal basis for
            the further processing of your data. In addition, we will delete this data if its processing was illegal for
            reasons unknown to us or if you have filed an objection against the processing and there are no primary
            legitimate interests for the processing. Your data will be deleted if we are legally obliged to do so. Our
            company has also implemented technical measures to inform all recipients of your data about your request for
            deletion or correction. This only applies if we have disclosed this data or made it public. All links,
            copies and replications of your personal data are to be deleted.
          </p>
          <p>
            If you have consented to the processing of your personal data, you are entitled to revoke your consent at any time with effect for the future. Revoking your consent does not make the data processing of the past unlawful.
          </p>
          <p>
            The transfer of data to our company is voluntary. However, these data are necessary for the further conclusion of the contract or to answer your enquiries. If you do not wish to disclose your data, the contract may not be valid or your enquiries may not be answered. The provision of data is required for the conclusion of the contract.
          </p>
          <p>
            The contact details of our company's Data Protection Officer are:{' '}
            <a href="mailto:datenschutzbeauftragter@erdinger.de">
              datenschutzbeauftragter@erdinger.de
            </a>
          </p>
          <p>
            You also have the right to complain to the competent supervisory authority about the processing of data by our company.
          </p>
          <p>The data protection authority responsible for our company is:</p>
          <p>
            Landesamt für Datenschutzaufsicht Promenade 27 (Schloss) 91522
            Ansbach, Germany Web:{' '}
            <a href="http://www.lda.bayern.de" target="_blank">
              http://www.lda.bayern.de
            </a>
          </p>
          <p>
            <b>Newsletter; Advertising</b>
            <br/>
            When signing up for our company's newsletter, the data you provide are used exclusively for this purpose.
          </p>
          <p>
            We require your name and a valid e-mail address for effective registration. In order to verify that a
            registration is actually carried out by the owner of an e-mail address, we use the "double opt-in"
            procedure. For this purpose, the order of the newsletter, the sending of a confirmation mail and the receipt
            of the requested reply are logged. The data are only used for sending the newsletter and are not passed on
            to third parties.
          </p>
          <p>
            You can revoke your consent to the storage of your data and its use for sending newsletters at any time.
            Each newsletter contains either a corresponding unsubscribe link or a separate unsubscribe option.{' '}
          </p>
          <p>
            <b>Data of minors</b>
            <br/>
            Personal data of children are not collected knowingly without explicitly pointing out that such data may
            only be transmitted with the consent of the parents. Our company only uses or discloses the personal data of
            children, where permitted by law, for the purpose of obtaining the legally required parental consent or for
            the protection of children. The applicable national statutory provisions are taken into account when using
            the term "child/children".
          </p>
          <p>
            <b>Log data</b>
            <br/>
            For technical reasons, when you access our company's website, your Internet browser automatically transfers
            the following data (hereinafter referred to as "log data") to our company's web server, which our company
            records in log files:
          </p>
          <ListWrap>
            <List>
              <ListItem>
                Date of access, time and duration of access, URL of the referring web page, retrieved file, amount of
                data transferred, browser type and version, operating system, domain name of your Internet access
                provider
              </ListItem>
            </List>
          </ListWrap>
          <p>
            This is exclusively information that does not allow conclusions to be drawn about the natural person. This information is technically necessary in order to display the website content you have requested correctly and is mandatory when using Internet offers. The log data are evaluated purely for statistical purposes in order to optimise our company's website and the technology behind it and are then deleted.
          </p>
          <p>
            The log data are stored separately from other data collected by our company during use.
          </p>
          <p>
            <b>
              Which data do we process when you visit our website?
            </b>
            <br/>
            Like many other websites, our company uses so-called "cookies". Cookies are small text files that are
            transferred from a website server to your hard drive. As a result, our company automatically receives
            certain data regarding your visit to our website. The cookies used by ERDINGER do not store any personal
            data. The cookies we use are therefore not assigned to a specific person and therefore not to you.
          </p>
          <p>
            Our company’s website can also be visited without cookies. Internet browsers are regularly set to accept
            cookies. You can deactivate the use of cookies at any time via the settings of your browser. By disabling
            cookies, some features of our company's website may no longer work. Most of the cookies we use are deleted
            from your hard drive after the end of the browser session (session cookies). Session cookies are required to
            provide you with login information across multiple pages. Other (persistent cookies) remain on your computer
            and allow us to recognise your computer on your next visit, making our website and offers appropriate for
            you and facilitating usage.
          </p>
          <p>
            <b>Pseudonymized user profiles</b>
            <br/>
            For purposes of advertising, market research, tailored design and to improve the services we provide, our
            company uses cookies to collect usage data about your website visit and the use of links in the newsletter.
            The collection of usage data and the creation of a usage profile are conducted pseudonymously using a cookie
            ID. Our company creates and stores these user profiles exclusively in a pseudonymised form. They are not
            merged with your personal data.
          </p>
          <p>
            <b>How can I deactivate cookies?</b>
            <br />
            We have compiled several links below that give you detailed information on how to deactivate cookies in the most common browsers:
          </p>
          <ListWrap>
            <List>
              {linkList.map((item, index) => (
                <ListItem key={index}>
                  <a href={item.href} target="_blank">
                    {item.name}
                  </a>
                </ListItem>
              ))}
            </List>
          </ListWrap>
          <p>
            <b>Web analytics using Matomo</b>
            <br />
            Scope of processing of personal data: For statistical analysis on this website we use the open source software "Matomo" (
            <a href="https://www.matomo.org" target="_blank">
              www.matomo.org
            </a>
            ). The software sets a cookie (a text file) on your computer, with which your browser can be recognized. If subpages of our website are called, the following data is stored:
          </p>
          <ListWrap>
            <List>
              {list2.map((item, index) => (
                <ListItem key={index}>{item}</ListItem>
              ))}
            </List>
          </ListWrap>
          <p>
          The data collected with Matomo is stored on our own servers. It is not passed on to third parties.
          </p>
          <iframe
            style={{
              border: 0,
              height: isMobile ? 350 : 200,
              width: '100%',
              margin: -8,
            }}
            src="https://analytics.erdinger.de/index.php?module=CoreAdminHome&action=optOut&language=en&backgroundColor=&fontColor=fff&fontSize=18px&fontFamily='sans-serif'"
          ></iframe>
          <p>
            <b>Legal basis</b>
            <br />
            The legal basis on which we process personal data using Matomo is Art. 6 para. 1 lit. f of the DSGVO.
          </p>
          <p>
            <b>Purpose of data processing</b>
            <br />
            We need the data to analyze the surfing behavior of users and to obtain information about the use of the individual components of the website. This enables us to continuously optimize the website and its user-friendliness. These purposes are our legitimate interest according to Art. 6 para. 1 lit. f DSGVO. By anonymizing the IP address, we take into account the interest of users in the protection of personal data. The data will never be used to personally identify the user of the website and will not be merged with other data.
          </p>
          <p>
            <b>Duration of storage</b>
            <br />
            The data is deleted when it is no longer needed for our purposes.
          </p>
          <p>
            <b>Possibility of objection</b>
            <br />
            You can object to the recording of data in the manner described above in three different ways:
          </p>
          <ListWrap>
            <List>
              {list3.map((item, index) => (
                <ListItem key={index}>{item}</ListItem>
              ))}
            </List>
          </ListWrap>
          <p>
            <b>Google Adwords Conversion Tracking</b>
            <br/>
            We use the service of Google Adwords to draw attention to our offers by displaying advertising media
            (so-called Google Adwords) on external websites. Based on the data of the advertising campaigns, we can
            measure the success of the advertising media. This allows us to display advertisements that are of interest
            to you and to make our website more interesting.
          </p>
          <p>
            The advertising media are submitted by Google via "ad servers" that run using cookies and are used to
            measure the success, such as display of the advertising measure and clicks of the users. If you access our
            website via a Google ad, Google stores a cookie on your computer, which generally expires after 30 days.
            There is no personal identification, only the recognition of your Internet browser.
          </p>
          <p>
            If a user has visited certain pages of an Adwords customer's website and the cookie stored on their computer
            has not yet expired, Google and the customer may recognize that the user clicked on the ad and was
            redirected to that page. Each Adwords customer is assigned a different cookie. Cookies can not be tracked
            via the websites of the Adwords customer. We do not collect and process any personal data ourselves via the
            aforementioned advertising measures. We only receive statistical evaluations from Google that help us
            identify which advertising media are effective. This does not allow any identification of the users.
          </p>
          <p>
            Due to the marketing tool used, your browser automatically establishes a direct connection with the Google
            server. We have no control over the extent and the further use of the data collected by Google by using this
            tool and can therefore only inform you according to our level of knowledge: the integration of AdWords
            Conversion informs Google that you have visited the relevant part of our website or clicked on one of our
            ads. If you are registered with a Google service, Google can assign the visit to your account. Even if you
            are not registered with Google or have not logged in, there is a chance that the provider can identify and
            store your IP address.
          </p>
          <p>
            You can prevent participation in this tracking process in a variety of ways: e.g. by setting your browser
            software to suppress cookies or disabling the conversion tracking cookies so that cookies from the domain „
            <a href="https://ads.google.com" target="_blank">
              www.googleadservices.com
            </a>
            " are blocked,{' '}
            <a href="https://www.google.de/settings/ads" target="_blank">
              https://www.google.de/settings/ads
            </a>
            . By disabling interest-based ads from providers that are part of the “About Ads” self-regulatory campaign
            is possible through{' '}
            <a href="https://optout.aboutads.info" target="_blank">
              https://optout.aboutads.info
            </a>{' '}
            or by permanently deactivating them in your Firefox, Internet Explorer or Chrome browsers &#40;
            <a href="http://www.google.com/settings/ads/plugin" target="_blank">
              http://www.google.com/settings/ads/plugin
            </a>
            &#41;. Kindly be aware that in this case you may not be able to use all features of this offer to their full
            extent.
          </p>
          <p>
            <b>Links to other websites</b>
            <br />
            If ERDINGER Weißbräu refers directly or indirectly to external websites ("links"), ERDINGER Weißbräu is only
            liable if it has precise knowledge of the contents and if it is technically possible and reasonable to
            prevent their use in the case of illegal content.
          </p>
          <p>
            ERDINGER Weißbräu hereby expressly declares that at the time of linking, the linked pages did not contain
            any illegal content. ERDINGER Weißbräu has no influence on the current and future design of the linked
            sites. ERDINGER Weißbräu hereby expressly dissociates itself from all changes in content that are made after
            the links have been set on the linked pages. This also applies to entries in the guest books, discussion
            forums and mailing lists provided by ERDINGER Weißbräu.
          </p>
          <p>
            Werner Brombach GmbH is not responsible for the content, availability, correctness and accuracy of linked
            sites, their offers, links or advertisements. ERDINGER Weißbräu is not liable for illegal, incorrect or
            incomplete content and in particular for damage resulting from the use or non-use of the information offered
            on the linked pages.
          </p>
          <p>
            <b>Youtube</b>
            <br/>
            Our company uses the video sharing platform YouTube, which is operated by YouTube LLC, 901 Cherry Ave. San
            Bruno, CA 94066, USA.
            <br/>
            <br/>
            YouTube is a platform that enables the playback of audio and video files. When the customer accesses a page
            on the company's website, YouTube's affiliate player will connect to YouTube to ensure a technical
            transmission of the video or audio file. When you connect to YouTube, data is transferred to YouTube.
            <br/>
            <br/>
            The purpose and scope of the data collection, the further processing and use of the data by YouTube as well
            as the related rights and setting options to protect the privacy of the customer, can be found in the
            privacy policy of YouTube:
            <br />{' '}
            <a
              href="https://support.google.com/youtube/answer/2801895?hl=de"
              target="_blank"
              rel="noreferrer noopener"
            >
              https://support.google.com/youtube/answer/2801895?hl=de
            </a>{' '}
          </p>
          <p>
            <b>Miscellaneous</b>
            <br/>
            Privatbrauerei ERDINGER Weißbräu Werner Brombach GmbH has the right to change this privacy policy in
            compliance with the applicable legal data protection regulations at any time. If ERDINGER Weißbräu
            commissions partner companies for the performance of certain services, the contracts with the partners will
            include the data protection provisions and the partners will be obliged to guarantee data protection. We
            take care of compliance with data protection regulations in all areas of our platform and our company. If
            you have questions, you can also contact us directly.
          </p>
        </div>
      </Section>
      <Footer stickyVisible={false} />
    </>
  )
}

export default IndexPage
